import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Address } from '@common/data-models/complex-types/address';
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { ClipboardTypes } from '@common/enums/clipboard-types';
import { LanguageSettings } from '@common/services/language-settings';
import { toDateTimeString } from '@src/suite/common/utils/date-utils';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { AppService } from '@suite/app.service';
import Handlebars from 'handlebars';
import { DateTime } from 'luxon';

import { UnsafePipe } from '../../../common/pipes/unsafe';

@Component({
    selector: 'st-clipboard',
    imports: [UnsafePipe],
    templateUrl: './clipboard.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClipboardComponent {
    appService = inject(AppService);
    languageSettings = inject(LanguageSettings);

    playbookService = inject(PlaybookService);
    readonly renderedHtml = signal<string>('');
    private static readonly initialized = signal(false);

    constructor() {
        if (!ClipboardComponent.initialized()) {
            Handlebars.registerHelper('date', text => {
                if (DateTime.isDateTime(text)) return toDateTimeString(text);
            });

            Handlebars.registerHelper('checklist', text => {
                if (text instanceof MultiValue) return text.toString();
            });

            Handlebars.registerHelper('staffidlist', text => {
                if (text instanceof MultiValue) return text.toString();
            });

            Handlebars.registerHelper('lookuplist', text => {
                if (text instanceof LookupList) return text.toString();
            });

            Handlebars.registerHelper('address', text => {
                if (text instanceof Address) return text.formattedAddress;
            });

            Handlebars.registerHelper('glang', function (text) {
                return this.languageSettings.get(text);
            });

            Handlebars.registerHelper('assignment', function (item) {
                return item.fn(this);
            });

            Handlebars.registerHelper('topic', function (item) {
                return item.fn(this);
            });

            Handlebars.registerHelper('script', function (item) {
                return item.fn(this);
            });

            Handlebars.registerHelper('rundown', function (item) {
                return item.fn(this);
            });

            Handlebars.registerHelper('note', function (this: StorytellingNoteRecord, item) {
                const context = {
                    ...this,
                    RawContent: this.RawContent ? (new DOMParser().parseFromString(this.RawContent, 'text/html').body.textContent ?? '') : '',
                };
                return item.fn(context);
            });

            ClipboardComponent.initialized.set(true);
        }

        ClipboardComponent.renderers.playbook = (data, type) => this.playbookService.copyToClipboardRenderer(data, type);
        ClipboardComponent.renderers.live = (data, type) => this.playbookService.copyToClipboardRenderer(data, type);
        ClipboardComponent.renderers.wastebin = (data, type) => this.playbookService.copyToClipboardRenderer(data, type);
        ClipboardComponent.renderers.templates = (data, type) => this.playbookService.copyToClipboardRenderer(data, type);
    }

    public async copy(
        data: StorytellingTopicRecord | StorytellingAssignmentRecord | StorytellingStoryRecord | StorytellingRundownRecord | StorytellingNoteRecord,
        type: ClipboardTypes,
        applicationName?: string
    ) {
        // Clear selection
        if (window.getSelection) {
            if (window.getSelection().empty) {
                // Chrome
                window.getSelection().empty();
            } else if (window.getSelection().removeAllRanges) {
                // Firefox
                window.getSelection().removeAllRanges();
            }
        }
        await this.renderHtml(data, type, applicationName);
        this.appService.applicationRef.tick();
        setTimeout(async () => {
            if (window.getSelection) {
                const el = document.getElementById('clipboardContainer');
                await navigator?.clipboard?.writeText(el.innerText);
            }
        }, 100);
    }

    public static renderers: Record<string, (any, ClipboardTypes) => Promise<string>> = {};

    private async renderHtml(
        data: StorytellingTopicRecord | StorytellingAssignmentRecord | StorytellingStoryRecord | StorytellingRundownRecord | StorytellingNoteRecord,
        type: ClipboardTypes,
        applicationName?: string
    ) {
        const res = await ClipboardComponent.renderers[applicationName || this.appService.applicationName](data, type);
        this.renderedHtml.set(res);
    }
}
